/**
 * usePublicProduct – fetch a single public product by slug (or id fallback)
 * within a producer context. Returns product + producer info for SEO.
 */
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface PublicProduct {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  price_ore: number;
  currency: string;
  status: string;
  pickup_instructions: string | null;
  purchase_mode: string;
  created_at: string;
  updated_at: string;
  producer_id: string;
}

export function usePublicProduct(producerId: string | undefined, productSlug: string | undefined) {
  const [product, setProduct] = useState<PublicProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!producerId || !productSlug) {
      setProduct(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetch() {
      setLoading(true);
      setError(null);

      try {
        // Statuses visible on public pages (aligned with shop list)
        const PUBLIC_STATUSES = ['public', 'sold_out', 'upcoming'];

        // Try by slug first
        const { data: bySlug, error: slugErr } = await supabase
          .from('producer_products')
          .select('*')
          .eq('producer_id', producerId!)
          .in('status', PUBLIC_STATUSES)
          .is('deleted_at', null)
          .eq('slug', productSlug!)
          .maybeSingle();

        if (!cancelled && bySlug && !slugErr) {
          setProduct(bySlug as any);
          setLoading(false);
          return;
        }

        // Fallback: try by ID (if productSlug looks like a UUID)
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(productSlug!);
        if (isUuid) {
          const { data: byId, error: idErr } = await supabase
            .from('producer_products')
            .select('*')
            .eq('id', productSlug!)
            .eq('producer_id', producerId!)
            .in('status', PUBLIC_STATUSES)
            .is('deleted_at', null)
            .maybeSingle();

          if (!cancelled && byId && !idErr) {
            setProduct(byId as any);
            setLoading(false);
            return;
          }
        }

        if (!cancelled) {
          setProduct(null);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Kunde inte hämta produkt.');
          setLoading(false);
        }
      }
    }

    fetch();
    return () => { cancelled = true; };
  }, [producerId, productSlug]);

  return { product, loading, error };
}
